import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { Check, Cloud, PenLine, Plus, Save, Table2, Trash2 } from 'lucide-react';
import type { Mailbox, Signature } from '@mailpilot/shared';
import { api } from '../../lib/api';
import { qk } from '../../lib/keys';
import { relativeTime } from '../../lib/format';
import { AccountBadge, Badge, Button, Card, CardBody, CardDivider, CardHeader, EmptyState, ErrorState, Notice, Skeleton, useConfirm, useToast } from '../../components/ui';
import { FormFooter } from '../../components/settings/SettingsPrimitives';
import { useMailboxes } from '../../hooks/useMailboxes';
import { SignatureEditor, emptySignatureDraft, type SignatureDraft } from '../../components/settings/SignatureEditor';
import { SettingsTitle } from './SettingsLayout';

function toDraft(s: Signature): SignatureDraft {
  return { id: s.id, name: s.name, mailboxId: s.mailboxId, text: s.text, html: s.html ?? '', isDefaultNew: s.isDefaultNew, isDefaultReply: s.isDefaultReply };
}

function sameDraft(a: SignatureDraft, b: SignatureDraft): boolean {
  return a.name === b.name && a.mailboxId === b.mailboxId && a.text === b.text && a.html === b.html && a.isDefaultNew === b.isDefaultNew && a.isDefaultReply === b.isDefaultReply;
}

/** The signature MailPilot will use for a mailbox: an account-specific default wins over an "any account" default. */
function defaultFor(sigs: Signature[], mailboxId: string, kind: 'isDefaultNew' | 'isDefaultReply'): { sig: Signature; inherited: boolean } | null {
  const own = sigs.find((s) => s.mailboxId === mailboxId && s[kind]);
  if (own) return { sig: own, inherited: false };
  const any = sigs.find((s) => s.mailboxId === null && s[kind]);
  return any ? { sig: any, inherited: true } : null;
}

function DefaultsTable({ sigs, mailboxes }: { sigs: Signature[]; mailboxes: Mailbox[] }) {
  if (!mailboxes.length) {
    return <p className="px-6 pb-6 text-[0.8125rem] text-muted">Connect an email account to set per-account defaults.</p>;
  }
  const cell = (d: ReturnType<typeof defaultFor>) =>
    d ? (
      <span className="flex flex-wrap items-center gap-1.5">
        <span className="font-medium text-ink">{d.sig.name || 'Untitled'}</span>
        {d.inherited && <Badge>any account</Badge>}
      </span>
    ) : (
      <span className="text-subtle">None</span>
    );
  return (
    <div className="overflow-x-auto px-6 pb-2 scroll-thin">
      <table className="w-full min-w-[320px] text-left text-[0.8125rem]">
        <thead>
          <tr className="text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">
            <th scope="col" className="py-3 pr-3 font-semibold">Account</th>
            <th scope="col" className="py-3 pr-3 font-semibold">New emails</th>
            <th scope="col" className="py-3 font-semibold">Replies</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line/70 border-t border-line/70">
          {mailboxes.map((m) => (
            <tr key={m.id}>
              <td className="py-3 pr-3">
                <AccountBadge color={m.color} label={m.displayName || m.email} />
              </td>
              <td className="py-3 pr-3">{cell(defaultFor(sigs, m.id, 'isDefaultNew'))}</td>
              <td className="py-3">{cell(defaultFor(sigs, m.id, 'isDefaultReply'))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SignatureSettings() {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const sigsQ = useQuery({ queryKey: qk.signatures, queryFn: () => api('GET /api/signatures') });
  const mailboxesQ = useMailboxes();
  const mailboxes = useMemo(() => mailboxesQ.data?.mailboxes ?? [], [mailboxesQ.data]);
  const mbMap = useMemo(() => new Map(mailboxes.map((m) => [m.id, m])), [mailboxes]);
  const sigs = useMemo(() => sigsQ.data?.signatures ?? [], [sigsQ.data]);

  const [selectedId, setSelectedId] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<SignatureDraft | null>(null);
  const [base, setBase] = useState<SignatureDraft | null>(null);

  // pick the first signature once loaded
  useEffect(() => {
    if (selectedId !== null || !sigsQ.data) return;
    if (sigs.length) {
      const d = toDraft(sigs[0]);
      setSelectedId(sigs[0].id);
      setDraft(d);
      setBase(d);
    }
  }, [sigsQ.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = !!draft && !!base && !sameDraft(draft, base);

  const guard = async () => (!dirty ? true : confirm({ title: 'Discard unsaved changes?', message: 'Your edits to this signature have not been saved.', confirmLabel: 'Discard', danger: true }));

  const select = async (s: Signature) => {
    if (s.id === selectedId) return;
    if (!(await guard())) return;
    const d = toDraft(s);
    setSelectedId(s.id);
    setDraft(d);
    setBase(d);
  };
  const startNew = async () => {
    if (!(await guard())) return;
    const d = { ...emptySignatureDraft(), isDefaultNew: sigs.length === 0, isDefaultReply: sigs.length === 0 };
    setSelectedId('new');
    setDraft(d);
    setBase(emptySignatureDraft());
  };

  const save = useMutation({
    mutationFn: async (d: SignatureDraft) => {
      const body = {
        name: d.name.trim(),
        mailboxId: d.mailboxId,
        text: d.text,
        html: d.html.trim() ? d.html : null,
        isDefaultNew: d.isDefaultNew,
        isDefaultReply: d.isDefaultReply,
      };
      if (d.id) return api('PATCH /api/signatures/:id', { params: { id: d.id }, body });
      return api('POST /api/signatures', { body });
    },
    onSuccess: (res) => {
      const d = toDraft(res.signature);
      setSelectedId(res.signature.id);
      setDraft(d);
      setBase(d);
      toast.success('Signature saved');
      // defaults on other signatures may have been cleared by the server
      void qc.invalidateQueries({ queryKey: qk.signatures });
    },
    onError: (e) => toast.error(e),
  });

  const del = useMutation({
    mutationFn: (id: string) => api('DELETE /api/signatures/:id', { params: { id } }),
    onSuccess: (_r, id) => {
      toast.success('Signature deleted');
      const rest = sigs.filter((s) => s.id !== id);
      if (rest.length) {
        const d = toDraft(rest[0]);
        setSelectedId(rest[0].id);
        setDraft(d);
        setBase(d);
      } else {
        setSelectedId(null);
        setDraft(null);
        setBase(null);
      }
      void qc.invalidateQueries({ queryKey: qk.signatures });
    },
    onError: (e) => toast.error(e),
  });

  const askDelete = async () => {
    if (!draft) return;
    if (!draft.id) {
      setSelectedId(null);
      setDraft(null);
      setBase(null);
      return;
    }
    const ok = await confirm({ title: `Delete “${draft.name || 'Untitled'}”?`, message: 'It will be removed from all your devices. Drafts already using it keep their current text.', confirmLabel: 'Delete', danger: true });
    if (ok) del.mutate(draft.id);
  };

  const canSave = !!draft && draft.name.trim().length > 0 && (draft.text.trim().length > 0 || draft.html.trim().length > 0) && (dirty || !draft.id);

  const title = <SettingsTitle title="Signatures" description="Create plain-text and HTML signatures, and choose different defaults per account for new emails and replies." />;

  if (sigsQ.isLoading) {
    return (
      <div>
        {title}
        <div className="space-y-6" aria-busy="true">
          <Skeleton className="h-44 w-full rounded-panel" />
          <Skeleton className="h-96 w-full rounded-panel" />
        </div>
      </div>
    );
  }
  if (sigsQ.error) {
    return (
      <div>
        {title}
        <Card>
          <ErrorState error={sigsQ.error} onRetry={() => void sigsQ.refetch()} />
        </Card>
      </div>
    );
  }

  return (
    <div>
      {title}
      <div className="space-y-6">
        <Card>
          <CardHeader
            icon={<PenLine className="h-5 w-5" />}
            title="Your signatures"
            description={sigs.length ? `${sigs.length} ${sigs.length === 1 ? 'signature' : 'signatures'}, synced to all your devices.` : 'Synced to all your devices.'}
            actions={
              (sigs.length > 0 || selectedId === 'new') && (
                <Button variant="primary" size="sm" icon={<Plus className="h-4 w-4" />} onClick={() => void startNew()}>
                  New signature
                </Button>
              )
            }
          />
          {sigs.length === 0 && selectedId !== 'new' ? (
            <EmptyState
              icon={<PenLine className="h-6 w-6" />}
              title="No signatures yet"
              description="Add a signature and MailPilot will append it to new emails and replies — once, without duplicates."
              actions={
                <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => void startNew()}>
                  Create a signature
                </Button>
              }
            />
          ) : (
            <CardBody className="!pt-5">
              <ul className="grid gap-2.5 sm:grid-cols-2" aria-label="Signatures">
                {sigs.map((s) => {
                  const mb = s.mailboxId ? mbMap.get(s.mailboxId) : null;
                  const active = s.id === selectedId;
                  return (
                    <li key={s.id}>
                      <button
                        type="button"
                        aria-current={active || undefined}
                        onClick={() => void select(s)}
                        className={clsx(
                          'flex w-full items-start gap-3 rounded-2xl px-3.5 py-3 text-left ring-1 transition-[background-color,box-shadow] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                          active ? 'bg-primary-soft ring-2 ring-primary' : 'bg-raised/80 ring-line/70 hover:bg-raised hover:ring-line',
                        )}
                      >
                        <span className={clsx('mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-surface', active ? 'text-primary-ink shadow-sm' : 'text-muted')}>
                          {active ? <Check className="h-4 w-4" aria-hidden /> : <PenLine className="h-4 w-4" aria-hidden />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className={clsx('block truncate text-[0.8125rem] font-medium', active ? 'text-primary-ink' : 'text-ink')}>{s.name || 'Untitled'}</span>
                          <span className={clsx('block truncate text-xs', active ? 'text-primary-ink/70' : 'text-muted')}>{s.text.split('\n')[0] || 'HTML only'} · edited {relativeTime(s.updatedAt)}</span>
                          <span className="mt-1.5 flex flex-wrap items-center gap-1.5">
                            {mb ? <AccountBadge color={mb.color} label={mb.displayName || mb.email} /> : <Badge>Any account</Badge>}
                            {s.isDefaultNew && <Badge tone="primary">New emails</Badge>}
                            {s.isDefaultReply && <Badge tone="info">Replies</Badge>}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
                {selectedId === 'new' && (
                  <li className="flex items-center gap-3 rounded-2xl border-2 border-dashed border-primary/40 bg-primary-soft/60 px-3.5 py-3 text-[0.8125rem] font-medium text-primary-ink">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-surface text-primary-ink shadow-sm">
                      <Plus className="h-4 w-4" aria-hidden />
                    </span>
                    New signature (unsaved)
                  </li>
                )}
              </ul>
            </CardBody>
          )}
        </Card>

        {draft && (
          <Card>
            <CardHeader
              icon={<PenLine className="h-5 w-5" />}
              title={draft.id ? `Edit “${draft.name || 'Untitled'}”` : 'New signature'}
              description={dirty ? 'You have unsaved changes.' : draft.id ? 'Changes apply to future emails only.' : 'Give it a name and at least a plain-text version.'}
              actions={
                <Button size="sm" variant="ghost" icon={<Trash2 className="h-4 w-4" />} loading={del.isPending} onClick={() => void askDelete()} className="text-danger-ink hover:!bg-danger-soft">
                  {draft.id ? 'Delete' : 'Discard'}
                </Button>
              }
            />
            <CardBody>
              <SignatureEditor draft={draft} onChange={setDraft} mailboxes={mailboxes} />
              <FormFooter className="mt-6" note={dirty ? 'Unsaved changes' : undefined}>
                {dirty && draft.id && (
                  <Button onClick={() => base && setDraft(base)} disabled={save.isPending}>
                    Revert
                  </Button>
                )}
                <Button variant="primary" icon={<Save className="h-4 w-4" />} loading={save.isPending} disabled={!canSave} onClick={() => save.mutate(draft)}>
                  {draft.id ? 'Save changes' : 'Create signature'}
                </Button>
              </FormFooter>
            </CardBody>
          </Card>
        )}

        <Card>
          <CardHeader icon={<Table2 className="h-5 w-5" />} title="Defaults by account" description="What MailPilot adds automatically. An account-specific default takes priority over an “any account” default." />
          <CardDivider className="mt-5" />
          {mailboxesQ.isLoading ? (
            <CardBody>
              <Skeleton className="h-24 w-full" />
            </CardBody>
          ) : mailboxesQ.error ? (
            <CardBody>
              <ErrorState compact error={mailboxesQ.error} onRetry={() => void mailboxesQ.refetch()} />
            </CardBody>
          ) : (
            <div className="pt-1">
              <DefaultsTable sigs={sigs} mailboxes={mailboxes} />
            </div>
          )}
        </Card>

        <Notice tone="info" icon={<Cloud className="h-4 w-4" />}>
          The server sanitises HTML when you save, so only safe formatting, links and images are kept. Each signature is applied exactly once per message.
        </Notice>
      </div>
    </div>
  );
}
